import { File_Key_Document_Role, File_Source } from "@qw/graphql";

export interface DisplayTypeFile {
  fileSource?: File_Source | null;
  keyDocumentRole?: File_Key_Document_Role | null;
  labels: Array<{ label: string }>;
}

const ROLE_TYPES: Record<File_Key_Document_Role, string> = {
  [File_Key_Document_Role.Policy]: "Policy",
  [File_Key_Document_Role.StampedPolicy]: "Stamped policy",
  [File_Key_Document_Role.ProcessedBinder]: "Binder - Processed",
  [File_Key_Document_Role.RedactedBinder]: "Binder - Redacted",
  [File_Key_Document_Role.ProcessedQuote]: "Quote - Processed",
  [File_Key_Document_Role.RedactedQuote]: "Quote - Redacted",
  [File_Key_Document_Role.RawQuote]: "Quote - Raw",
  [File_Key_Document_Role.Acord_125]: "ACORD 125",
};

// Classifier sub-document labels, from `SubmissionDocumentLabels` in the API's `tags/known-labels.ts`.
const SUB_DOCUMENT_TYPES = new Map([
  ["loss-run", "Loss run"],
  ["supplemental", "Supplemental"],
  ["state-document", "State document"],
  ["sov", "SOV"],
  ["surplus-lines-form", "Surplus lines form"],
  ["additional-coverages", "Additional coverages"],
  ["applied-125pcis", "Applied 125 PCIS"],
  ["mvr", "MVR"],
]);

const SOURCE_TYPES: Partial<Record<File_Source, string>> = {
  [File_Source.PolicyPdf]: "Policy",
  [File_Source.QuotePdf]: "Quote",
  [File_Source.RawQuotePdf]: "Quote",
  [File_Source.RedactedQuote]: "Quote",
  [File_Source.LossRunDoc]: "Loss run",
  [File_Source.GenerateAcord]: "ACORD",
  [File_Source.GeneratedSov]: "SOV",
  [File_Source.InscipherStateDocument]: "State document",
};

// Classifier document types, plus the lowercase `KnownFileLabels` forms written by quote and policy code paths.
const CLASSIFIER_TYPES = new Map([
  ["quote", "Quote"],
  ["policy", "Policy"],
  ["binder", "Binder"],
  ["invoice", "Invoice"],
  ["noc", "NOC"],
  ["finance", "Finance"],
]);

const ACORD_LABEL = /^acord-(\d+)$/;

const subDocumentType = (label: string) => {
  const normalized = label.toLowerCase();
  const acord = ACORD_LABEL.exec(normalized);
  return acord ? `ACORD ${acord[1]}` : SUB_DOCUMENT_TYPES.get(normalized);
};

const classifierType = (label: string) => CLASSIFIER_TYPES.get(label.toLowerCase());

/** The display type a single raw label stands for, or `undefined` when it names no document type. */
export const labelDisplayType = (label: string): string | undefined => subDocumentType(label) ?? classifierType(label);

/** One document type per file: key-document role, then sub-document label, then `fileSource`, then classifier type. */
export const fileDisplayType = (file: DisplayTypeFile): string | null => {
  if (file.keyDocumentRole) {
    return ROLE_TYPES[file.keyDocumentRole];
  }

  const labels = file.labels.map(({ label }) => label);
  const fromSource = file.fileSource ? SOURCE_TYPES[file.fileSource] : undefined;

  return labels.map(subDocumentType).find(Boolean) ?? fromSource ?? labels.map(classifierType).find(Boolean) ?? null;
};

/** Splits a role type like "Quote - Redacted" into its plain type and its processing state. */
export const displayTypeParts = (displayType: string) => {
  const [type, state] = displayType.split(" - ");
  return { type, state };
};

/** Raw labels worth showing beside the chip: any label naming the chip's own type is redundant. */
export const labelsBesideChip = <T extends { label: string }>(labels: readonly T[], displayType: string | null) =>
  labels.filter(({ label }) => !displayType || labelDisplayType(label) !== displayTypeParts(displayType).type);
